import React, { useCallback, useEffect, useRef, useState } from "react";
import CareerIntro from "components/career-intro/career-intro";
import ContentCard from "components/card/content-card";
import Button from "components/button/button";
import GlimpseProgressDots from "components/progress-dots/progress-dots.component";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ContributionCard,
  EducationCard,
  DayinLifeCard,
  ProsConsCard,
  SalaryCard,
  CareerTrendCard,
  DidYouKnowCard,
  ImpactCard,
  Reprioritise,
  GreatWork,
  ExpertVoices,
  Quizzes,
} from "components/career-details/career-details.component";
import {
  CareerTrends,
  DidYouKnow,
  Impacts,
  PageNumberState,
} from "interfaces/CareerInfoInterface";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import Loading from "components/loading/loading.component";
import {
  completeDeeperDive,
  deeperDiveLevelChanged,
} from "services/deeper-dive.service";
import { CareerCategory, References, VIdeoStatus } from "enums/glimps.enums";
import { ListPrioritize } from "services/on-board";

import {
  IDeeperDiveExpertVoice,
  IDeeperDiveProsCons,
} from "interfaces/CareerInterface";
import { displayError } from "components/Toast/toast.component";
import VideoFallbackModal from "components/video-fallback-modal/video-fallback-modal.component";
import { useVideoCompletion } from "hooks/useVideoCompletion";
import GlimpsePopup from "components/popup/popup.component";
import PrioritizeComponent from "components/prioritize/prioritize.component";
import StartDive from "./start-dive";
import { noPreviewImage } from "utills/image";
import { getUserBadgeEntries } from "services/badges.service";

const DeeperDive: React.FC = () => {
  let { param } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [quizCurrentSlide, setQuizCurrentSlide] = useState(0);
  const [isQuiz, setIsQuiz] = useState<boolean>(false);
  const [enableNextButton, setEnableNextButton] = useState<boolean>(false);
  const [enableDayInLife, setEnableDayInLife] = useState<boolean>(false);
  const [enableDidYouKnow, setEnableDidYouKnow] = useState<boolean>(false);
  const [enableProsCons, setEnableProsCons] = useState<boolean>(false);
  const [enableEducation, setEnableEducation] = useState<boolean>(false);
  const [slideCount] = useState(11);
  const [intervalCount, setIntervalCount] = useState<number>(0);
  const [currentVideo, setCurrentVideo] = useState<IDeeperDiveExpertVoice>();
  const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
  const [displayPopup, setDisplayPopup] = useState<boolean>(false);
  const [attemptedReprioritizeSave, setAttemptedReprioritizeSave] =
    useState<boolean>(false);
  const [displayLinkPopup, setDisplayLinkPopup] = useState<{
    type: References | null;
    isOpen: boolean;
  }>({ type: null, isOpen: false });
  const [rightAnswer, setRightAnswer] = useState<boolean>(false);
  const [pageNumber, setPageNumber] = useState<PageNumberState>({
    dayInLife: 0,
    didyouknow: 0,
    education: 0,
    prosCons: 0,
  });
  const [isDisplay, setIsDisplay] = useState<boolean>(false);
  const [completedVoiceIds, setCompletedVoiceIds] = useState<Set<string>>(new Set());

  const {
    showFallbackModal,
    fallbackLoading,
    checkAutoComplete,
    ensureCompletion,
    handleFallbackMark,
    dismissFallback,
    cleanup,
  } = useVideoCompletion();

  useEffect(() => {
    const deepdiveLevel = location.state?.deepdive_level;
    if (deepdiveLevel) {
      const level = Number(deepdiveLevel);
      if (level === 11 || level === 12) {
        setCurrentSlide(level - 2);
      } else setCurrentSlide(level - 1);
    } else {
      setCurrentSlide(Number(deepdiveLevel ?? 0));
    }
  }, [location]);
  useEffect(() => {
    const deepdiveLevel = location.state?.deepdive_level;
    if (location.state?.setCurrentSlide) {
      setQuizCurrentSlide(Number(deepdiveLevel ?? 0) - 2);
    } else {
      setQuizCurrentSlide(Number(deepdiveLevel ?? 0));
    }
  }, [location]);

  useEffect(() => {
    if (enableDayInLife) {
      enableNext();
    }
  }, [enableDayInLife]);

  useEffect(() => {
    if (enableDidYouKnow) {
      enableNext();
    }
  }, [enableDidYouKnow]);

  useEffect(() => {
    if (enableEducation) {
      enableNext();
    }
  }, [enableEducation]);

  useEffect(() => {
    if (enableProsCons) {
      enableNext();
    }
  }, [enableProsCons]);

  // Cleanup retry timers on unmount
  useEffect(() => cleanup, [cleanup]);

  const { data: reprioritizeListData } = useQuery({
    queryKey: ["ListPrioritize"],
    queryFn: () => ListPrioritize(),
    retry: false,
    refetchOnWindowFocus: false,
    enabled: displayPopup,
  });
  const reprioritizeUnsortedCount =
    reprioritizeListData?.filter(
      (x) => x.category === CareerCategory.unsorted
    ).length ?? 0;
  const reprioritizeHasUnsorted = reprioritizeUnsortedCount > 0;

  useEffect(() => {
    if (!reprioritizeHasUnsorted && attemptedReprioritizeSave) {
      setAttemptedReprioritizeSave(false);
    }
  }, [reprioritizeHasUnsorted, attemptedReprioritizeSave]);
  const {
    data: careerInfo,
    isFetching,
    error,
    refetch: refetchDeeperDive,
  } = useSuspenseQuery({
    queryKey: ["GetCareerForDeeperDive", param, currentSlide],
    queryFn: () => {
      return deeperDiveLevelChanged(param as string, currentSlide + 1);
    },
    retry: 0,
    refetchOnWindowFocus: false,
    networkMode: "always",
  });
  enableNext();

  const markVoiceCompleted = useCallback(
    (voiceId: string) => {
      setCompletedVoiceIds((prev) => {
        const updated = new Set(prev);
        updated.add(voiceId);
        return updated;
      });
      const isAllCompleted = careerInfo?.user_career_expert_voices?.every(
        (x: any) =>
          x.deeper_dive_status === VIdeoStatus.Completed ||
          completedVoiceIds.has(x.id) ||
          x.id === voiceId
      );
      setEnableNextButton(!!isAllCompleted);
    },
    [careerInfo, completedVoiceIds]
  );

  const markAsComplete = useMutation({
    mutationKey: ["CompleteDeeperDive"],
    mutationFn: (id: string) => completeDeeperDive(id),
    retry: 0,
    onSuccess: async (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["my-earned-badges"] });
      navigate(
        `/user-careers/careers/career-insights/${careerInfo.career.id}`,
        { replace: true }
      );
    },
  });

  useEffect(() => {
    if (
      careerInfo?.user_career_expert_voices &&
      careerInfo?.user_career_expert_voices.length > 0
    ) {
      setCurrentVideo(careerInfo.user_career_expert_voices[0]);
      // Seed completedVoiceIds from server state
      const alreadyCompleted = careerInfo.user_career_expert_voices
        .filter((x) => x.deeper_dive_status === VIdeoStatus.Completed)
        .map((x) => x.id);
      if (alreadyCompleted.length > 0) {
        setCompletedVoiceIds((prev) => {
          const updated = new Set(prev);
          alreadyCompleted.forEach((id) => updated.add(id));
          return updated;
        });
      }
    }
  }, [careerInfo]);

  useEffect(() => {
    if (error) {
      handlePrev();
    }
  }, [error]);

  const quizComponents = () => {
    switch (quizCurrentSlide) {
      case 1:
        return (
          <Quizzes
            currentSlide={1}
            key={"Question1"}
            question={careerInfo.quizzes ? careerInfo.quizzes[0].question : ""}
            answers={careerInfo.quizzes ? careerInfo.quizzes[0].answers : []}
            answerChoosed={(data) => {
              setRightAnswer(data.is_correct);
            }}
          />
        );
      case 2:
        return (
          <Quizzes
            currentSlide={2}
            key={"Question2"}
            question={careerInfo.quizzes ? careerInfo.quizzes[1].question : ""}
            answers={careerInfo.quizzes ? careerInfo.quizzes[1].answers : []}
            answerChoosed={(data) => {
              setRightAnswer(data.is_correct);
            }}
          />
        );
      case 3:
        return (
          <Quizzes
            currentSlide={3}
            key={"Question3"}
            question={careerInfo.quizzes ? careerInfo.quizzes[2].question : ""}
            answers={careerInfo.quizzes ? careerInfo.quizzes[2].answers : []}
            answerChoosed={(data) => {
              setRightAnswer(data.is_correct);
            }}
          />
        );
    }
  };

  const conditionalComponent = () => {
    switch (currentSlide) {
      case 0:
        return (
          <ContributionCard
            key={"contribution"}
            from="DeeperDive"
            careerName={careerInfo?.career.name}
            contribution_url={careerInfo?.career.contribution_url}
          />
        );
      case 1:
        return (
          <ExpertVoices
            key={"export"}
            from="DeeperDive"
            expertVoiceInfo={
              careerInfo?.user_career_expert_voices
                ? careerInfo.user_career_expert_voices
                : []
            }
            selectedVideo={currentVideo?.expert_voice.voice_url}
            videoSelected={(video) => {
              setVideoCurrentTime(0);
              setCurrentVideo(video);
            }}
            deeperDiveClass={true}
            onVideoStarted={() => {}}
            onProgress={(data) => {
              setVideoCurrentTime(data.playedSeconds);
              if (currentVideo?.id) {
                if (checkAutoComplete(currentVideo.id, data.playedSeconds, data.played)) {
                  markVoiceCompleted(currentVideo.id);
                }
              }
            }}
            onPaused={() => {}}
            onVideoEnded={() => {
              if (currentVideo?.id) {
                ensureCompletion(currentVideo.id);
                markVoiceCompleted(currentVideo.id);
              }
            }}
          />
        );
      case 2:
        return (
          careerInfo?.days_in_life && (
            <DayinLifeCard
              from="DeeperDive"
              // daysInLifeInfo={careerInfo.days_in_life}
              daysInLifeInfo={[...careerInfo.days_in_life].sort(
                (a, b) => a.order - b.order
              )}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              checkNextEnable={setEnableDayInLife}
            />
          )
        );
      case 3:
        return (
          <ProsConsCard
            key={"proscons"}
            from="DeeperDive"
            prosconsInfo={careerInfo?.pros_cons as IDeeperDiveProsCons}
            deeperDiveClass={true}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            checkNextEnable={setEnableProsCons}
          />
        );

      case 4:
        return (
          <DidYouKnowCard
            key={"didyouknow"}
            from="DeeperDive"
            checkNextEnable={setEnableDidYouKnow}
            handleOpen={() => {
              setDisplayLinkPopup({
                type: References.DidYouKnow,
                isOpen: true,
              });
            }}
            didyouKnowsInfo={
              (careerInfo?.did_you_knows
                ? careerInfo.did_you_knows
                : []) as DidYouKnow[]
            }
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
          />
        );
      case 5:
        return (
          careerInfo?.educations && (
            <EducationCard
              key={"education"}
              checkNextEnable={setEnableEducation}
              education={careerInfo?.educations ? careerInfo.educations : []}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              from={"DeeperDive"}
            />
          )
        );
      case 6:
        return (
          <>
            {careerInfo?.salaries && (
              <SalaryCard
                key={"salary"}
                from={"DeeperDive"}
                handleOpen={() => {
                  setDisplayLinkPopup({
                    type: References.Salary,
                    isOpen: true,
                  });
                }}
                advance={Number(careerInfo.salaries.advance)}
                median={Number(careerInfo.salaries.median)}
                starting={Number(careerInfo.salaries.starting)}
                height="320px"
                width="250px"
              />
            )}
          </>
        );

      case 7:
        return (
          <CareerTrendCard
            key={"careertrend"}
            from={"DeeperDive"}
            handleOpen={() => {
              setDisplayLinkPopup({
                type: References.CareerDemand,
                isOpen: true,
              });
            }}
            careerTrendInfo={
              (careerInfo?.trends ? careerInfo.trends : []) as CareerTrends[]
            }
            deeperDiveClass={true}
          />
        );
      case 8:
        return (
          <ImpactCard
            key={"impact"}
            from="DeeperDive"
            handleOpen={() => {
              setDisplayLinkPopup({
                type: References.ImpactQuote,
                isOpen: true,
              });
            }}
            impactInfo={
              (careerInfo?.impacts ? careerInfo.impacts : []) as Impacts[]
            }
          />
        );
      case 9:
        return (
          <Reprioritise
            onYes={() => {
              // displayError("Implement On yes");
              setDisplayPopup(true);
            }}
            onNo={handleNext}
          />
        );
      case 10:
        return (
          <GreatWork
            isQuizEnable={careerInfo?.enable_quiz as boolean}
            careerName={careerInfo.career.name}
          />
        );
      default:
        return <Loading text="Unable to load data" />;
    }
  };

  const handleNext = async (isEnableQuiz: boolean) => {
    setEnableNextButton(false);
    if (isQuiz && quizCurrentSlide === 3) {
      markAsComplete.mutateAsync(careerInfo.id);

      return;
    } else if (isEnableQuiz) {
      setIsQuiz(true);
      setQuizCurrentSlide(1);
    } else if (isQuiz) {
      await setQuizCurrentSlide((prev) => prev + 1);
      setRightAnswer(false);

      return;
    }
    await setCurrentSlide((prev) => prev + 1);
    refetchDeeperDive();
  };

  const handlePrev = async () => {
    if (isQuiz) {
      if (quizCurrentSlide <= 1) return;
      await setQuizCurrentSlide((prev) => prev - 1);
      setRightAnswer(false);
    } else {
      if (currentSlide > 0) {
        await setCurrentSlide((prev) => prev - 1);
        refetchDeeperDive();
      }
    }
  };

  function enableNext() {
    setTimeout(() => {
      switch (currentSlide) {
        case 1:
          // Check both server state and locally tracked completions
          let allUnlocked = careerInfo?.user_career_expert_voices?.find(
            (x) =>
              x.deeper_dive_status === VIdeoStatus.Unlocked &&
              !completedVoiceIds.has(x.id)
          );
          setEnableNextButton(!!!allUnlocked);
          break;
        case 2:
          setEnableNextButton(enableDayInLife);
          break;
        case 3:
          setEnableNextButton(enableProsCons);
          break;
        case 4:
          setEnableNextButton(enableDidYouKnow);
          break;
        case 5:
          setEnableNextButton(enableEducation);
          break;
        default:
          setEnableNextButton(true);
          break;
        // return true;
      }
    }, 0); // Unlock Next button after 2 second
  }

  return (
    <>
      {isDisplay ? (
        <div className="w-full h-full flex flex-col">
          <GlimpsePopup
            title=""
            popup={displayLinkPopup?.isOpen}
            onClose={() => {
              setDisplayLinkPopup({ isOpen: false, type: null });
            }}
            showCross={true}
            customClass="max-w-[480px]"
          >
            <>
              <div>
                <div className="mb-6">
                  <h2 className="text-2xl font-semibold text-dark-blue mb-2">
                    {careerInfo.career.name}: Sources
                  </h2>
                  <p className="text-sm font-normal leading-5 text-gray-dark">
                    Listed below are sources we used to gather data about this
                    career.
                  </p>
                </div>
                <div className="flex flex-col gap-3">
                  {careerInfo.references?.map((item, index) => {
                    return (
                      <a
                        key={index}
                        href={item.link as string}
                        target="_blank"
                        rel="noreferrer"
                        title={item.title}
                        className="text-sm text-primary hover:brightness-90 break-all"
                      >
                        {item.link as string}
                      </a>
                    );
                  })}
                </div>
              </div>
            </>
          </GlimpsePopup>
          <GlimpsePopup
            customClass={"w-[80vw] max-w-[900px] h-[80vh]"}
            onClose={() => {
              setDisplayPopup(false);
              setAttemptedReprioritizeSave(false);
            }}
            popup={displayPopup}
            showCross={true}
            title={"Reprioritize"}
          >
            <PrioritizeComponent
              showUnsortedError={
                attemptedReprioritizeSave && reprioritizeHasUnsorted
              }
            />
            <div className="flex justify-end px-5 mt-4">
              <Button
                variant="primary"
                onClick={() => {
                  if (reprioritizeHasUnsorted) {
                    setAttemptedReprioritizeSave(true);
                    return;
                  }
                  setDisplayPopup(false);
                  setAttemptedReprioritizeSave(false);
                  handleNext(false);
                }}
              >
                Finish
              </Button>
            </div>
          </GlimpsePopup>

          <div className="flex justify-center w-full flex-1">
            <div className="flex flex-col gap-8 max-w-[875px] w-full">
              <CareerIntro
                key={`Deeper_Dive_Header_${currentSlide}`}
                title={careerInfo.career.name}
                subtitle={careerInfo.career.superpower}
                description={careerInfo.career.description}
                image={
                  careerInfo.career.icon_url
                    ? careerInfo.career.icon_url
                    : noPreviewImage
                }
              />
              <ContentCard>
                {careerInfo && !isQuiz ? (
                  <GlimpseProgressDots
                    key={"ConditionalDeeperDive"}
                    totalSlides={slideCount}
                    currentSlide={currentSlide + 1}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    onClose={() => setIsDisplay(false)}
                    enableQuiz={careerInfo.enable_quiz as boolean}
                    enableSorting={false}
                    isQuiz={isQuiz}
                    handleFinish={() => {
                      markAsComplete.mutateAsync(careerInfo.id);
                    }}
                    enableNext={enableNextButton}
                    handleSortNow={() => {}}
                    maxHeight={true}
                    hideButtons={true}
                  >
                    {conditionalComponent()}
                  </GlimpseProgressDots>
                ) : (
                  <GlimpseProgressDots
                    key={"QuizDeeperDive"}
                    totalSlides={3}
                    currentSlide={quizCurrentSlide}
                    onNext={handleNext}
                    onPrev={handlePrev}
                    onClose={() => setIsDisplay(false)}
                    enableQuiz={careerInfo.enable_quiz as boolean}
                    isQuiz={isQuiz}
                    handleFinish={() => {
                      markAsComplete.mutateAsync(careerInfo.id);
                    }}
                    enableSorting={false}
                    enableNext={rightAnswer}
                    handleSortNow={() => {}}
                    maxHeight={true}
                    hideButtons={true}
                  >
                    {quizComponents()}
                  </GlimpseProgressDots>
                )}
              </ContentCard>
            </div>
          </div>

          <div className="flex justify-between items-center mt-auto pt-4 pb-4">
            {currentSlide === 0 && !isQuiz ? (
              <Button variant="secondary" onClick={() => setIsDisplay(false)}>
                Close
              </Button>
            ) : (
              <Button
                variant="secondary"
                onClick={handlePrev}
                disabled={isQuiz && quizCurrentSlide <= 1}
              >
                Back
              </Button>
            )}

            <div className="render-dots flex items-center">
              {Array.from({ length: isQuiz ? 3 : slideCount }, (_, i) => (
                <div
                  key={i + 1}
                  className={`rounded-full ${
                    (isQuiz ? quizCurrentSlide : currentSlide + 1) === i + 1
                      ? "bg-green w-[33px] h-[8px]"
                      : "bg-lightGray mx-[5px] h-[8px] w-[8px]"
                  }`}
                />
              ))}
            </div>

            {currentSlide + 1 === slideCount && !isQuiz ? (
              <Button
                variant="primary"
                onClick={
                  !careerInfo.enable_quiz
                    ? () => markAsComplete.mutateAsync(careerInfo.id)
                    : () => handleNext(true)
                }
                disabled={!enableNextButton}
              >
                {!careerInfo.enable_quiz ? "Finish" : "Start Quiz"}
              </Button>
            ) : isQuiz && quizCurrentSlide === 3 ? (
              <Button
                variant="primary"
                onClick={() => markAsComplete.mutateAsync(careerInfo.id)}
                disabled={!rightAnswer}
              >
                Finish
              </Button>
            ) : (
              <Button
                variant="primary"
                onClick={() => {
                  if (isQuiz) {
                    handleNext(false);
                  } else {
                    handleNext(false);
                  }
                }}
                disabled={isQuiz ? !rightAnswer : !enableNextButton}
              >
                Next
              </Button>
            )}
          </div>
        </div>
      ) : (
        <StartDive
          title={careerInfo.career.name}
          subtitle={careerInfo.career.superpower}
          icon_url={careerInfo.career.icon_url}
          description={careerInfo.career.description}
          id={location.state.career_id}
          setIsDisplay={setIsDisplay}
          // is_deep_dive={careerInfo.career.is_deep_dive}
          isDeepDive={careerInfo.is_deep_dive}
        />
      )}

      <VideoFallbackModal
        isOpen={showFallbackModal}
        onMarkViewed={() =>
          handleFallbackMark((videoId) => markVoiceCompleted(videoId))
        }
        onKeepWatching={dismissFallback}
        isLoading={fallbackLoading}
      />
    </>
  );
};

export default DeeperDive;
